import { Fragment, useEffect, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { Command } from "cmdk";
import { ChevronRight, Search } from "lucide-react";

import type { Candidate, CandidateGroup } from "../command-palette/types";
import { useCommandPalette } from "../react/hooks/useCommandPalette";

const SEARCH_DEBOUNCE_MS = 100;

type Mode = "default" | "menus" | "conversations";

// Workspace command palette. Visual mirrors the canonical prototype:
//  - cmdk's Command.Dialog hosts the Radix Dialog (label-only aria, no
//    visible Title/Description),
//  - first keystroke detects "/" → menus mode, "@" → conversations mode;
//    the leading character becomes a coloured chip and the placeholder
//    text shifts,
//  - Backspace at an empty input in a prefix mode exits back to default,
//  - groups + items render straight from CommandPaletteEngine.search().
export function CommandPalette() {
    const { open, hide, engine } = useCommandPalette();

    const [mode, setMode] = useState<Mode>("default");
    const [displayValue, setDisplayValue] = useState("");
    const [groups, setGroups] = useState<CandidateGroup[]>([]);

    // Reset each time the palette opens — invocation always starts blank.
    useEffect(() => {
        if (open) {
            setMode("default");
            setDisplayValue("");
        }
    }, [open]);

    // Engine query — debounced to collapse keystroke bursts. The mode chip
    // is purely visual; the engine itself routes by prefix via the actual
    // typed character (we prepend the chip's prefix back when calling
    // engine.search so existing engine routing still applies).
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (!open) return;
        if (timerRef.current !== null) clearTimeout(timerRef.current);
        const effectiveQuery =
            mode === "menus"
                ? `/${displayValue}`
                : mode === "conversations"
                    ? `@${displayValue}`
                    : displayValue;
        timerRef.current = setTimeout(() => {
            Promise.resolve(engine.search(effectiveQuery))
                .then((next) => setGroups(Array.isArray(next) ? next : []))
                .catch(() => setGroups([]));
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [engine, open, displayValue, mode]);

    // First keystroke that is `/` or `@` switches mode + clears the visible
    // input so the chip replaces the prefix character.
    const handleValueChange = (value: string) => {
        if (mode === "default" && displayValue === "") {
            if (value === "/") {
                setMode("menus");
                setDisplayValue("");
                return;
            }
            if (value === "@") {
                setMode("conversations");
                setDisplayValue("");
                return;
            }
        }
        setDisplayValue(value);
    };

    const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && displayValue === "" && mode !== "default") {
            e.preventDefault();
            setMode("default");
        }
    };

    const handleSelect = (candidate: Candidate) => {
        try {
            void candidate.onPick();
        } finally {
            hide();
        }
    };

    const placeholder =
        mode === "menus"
            ? "Search menus…"
            : mode === "conversations"
                ? "Search conversations…"
                : "Type a command or search anything…";

    return (
        <Command.Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) hide();
            }}
            label="Command palette"
            overlayClassName="cmdk__overlay"
            contentClassName="cmdk__content"
            shouldFilter={false}
            loop
        >
            <div className="cmdk__head">
                <Search size={16} className="cmdk__head-icon" />
                {mode !== "default" ? (
                    <span className={`cmdk__mode-chip cmdk__mode-chip--${mode}`}>
                        {mode === "menus" ? "/ menus" : "@ conversations"}
                    </span>
                ) : null}
                <Command.Input
                    value={displayValue}
                    onValueChange={handleValueChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    autoFocus
                />
                <span className="cmdk__head-esc">ESC</span>
            </div>

            <Command.List className="cmdk__list">
                <Command.Empty className="cmdk__empty">No results found.</Command.Empty>

                {groups.map((group) => (
                    <Command.Group key={group.key} heading={group.label} className="cmdk__group">
                        {group.items.map((candidate) => {
                            const value =
                                candidate.crumb && candidate.crumb.length > 0
                                    ? candidate.crumb.join(" / ")
                                    : candidate.title;
                            return (
                                <Command.Item
                                    key={candidate.id}
                                    value={`${candidate.id} ${value}`}
                                    className="cmdk__item"
                                    onSelect={() => handleSelect(candidate)}
                                >
                                    <CandidateRow candidate={candidate} />
                                </Command.Item>
                            );
                        })}
                    </Command.Group>
                ))}
            </Command.List>

            <div className="cmdk__tip">
                <strong>TIP</strong>
                <span> search for </span>
                <code className="cmdk__tip-token cmdk__tip-token--menus">/menus</code>
                <span> or </span>
                <code className="cmdk__tip-token cmdk__tip-token--chats">@conversations</code>
            </div>
        </Command.Dialog>
    );
}

// Render a single candidate row. Two layouts:
//  - With `crumb`: chevron-separated app › section › leaf (canonical /-mode).
//  - Otherwise: stacked title + subtitle (default / @-mode).
// The leading icon slot renders `candidate.icon` when provided; the tile
// background is coloured via `data-color={candidate.accentColorIndex}` against
// the SCSS app-color palette.
function CandidateRow({ candidate }: { candidate: Candidate }) {
    const hasCrumb = candidate.crumb !== undefined && candidate.crumb.length > 0;
    const tileMode = candidate.accentColorIndex !== undefined;

    return (
        <>
            <span
                className={tileMode ? "cmdk__item-icon cmdk__item-icon--tile" : "cmdk__item-icon"}
                data-color={candidate.accentColorIndex}
                aria-hidden="true"
            >
                {candidate.icon}
            </span>
            {hasCrumb ? (
                <span className="cmdk__item-label">
                    {candidate.crumb!.map((part, idx) => (
                        <Fragment key={`${part}-${idx}`}>
                            {idx > 0 ? <ChevronRight size={11} className="cmdk__item-crumb-sep" /> : null}
                            <span
                                className={
                                    idx === candidate.crumb!.length - 1
                                        ? "cmdk__item-crumb-leaf"
                                        : "cmdk__item-crumb"
                                }
                            >
                                {part}
                            </span>
                        </Fragment>
                    ))}
                </span>
            ) : (
                <span className="cmdk__item-label cmdk__item-label--stack">
                    <span className="cmdk__item-record-name">{candidate.title}</span>
                    {candidate.subtitle !== undefined ? (
                        <span className="cmdk__item-record-meta">{candidate.subtitle}</span>
                    ) : null}
                </span>
            )}
        </>
    );
}
